import { collection, deleteDoc, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

const collections = {
  leads: 'crmLeads', contacts: 'crmContacts', deals: 'crmDeals', tasks: 'crmTasks',
  activities: 'crmActivities', integrations: 'crmIntegrations', stages: 'crmPipelineStages',
};
const normalizeRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');
const timestampValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const ensure = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Super Admin authentication is required.');
  const snap = await getDoc(doc(db, 'users', uid));
  const data = snap.exists() ? snap.data() || {} : {};
  if (!['super_admin', 'superadmin'].includes(normalizeRole(data.role || data.userRole)) || data.isBlocked === true) {
    throw new Error('Only Super Admin can manage CRM data.');
  }
  return uid;
};


const cleanText = (value) => String(value || '').trim();
const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

async function assertActiveOwner(ownerId) {
  if (!ownerId) return;
  const [employeeSnap, userSnap] = await Promise.all([
    getDoc(doc(db, 'platformEmployees', ownerId)),
    getDoc(doc(db, 'users', ownerId)),
  ]);
  const employee = employeeSnap.exists() ? employeeSnap.data() || {} : {};
  const user = userSnap.exists() ? userSnap.data() || {} : {};
  if (!employeeSnap.exists() || !userSnap.exists() || employee.isActive === false || employee.status === 'inactive' || user.isActive === false || user.isBlocked === true) {
    throw new Error('Assigned CRM owner is no longer an active platform employee.');
  }
}

async function assertStage(stageName) {
  const value = cleanText(stageName);
  if (!value) throw new Error('Pipeline stage is required.');
  const snap = await getDocs(query(collection(db, collections.stages), where('name', '==', value), limit(1)));
  if (snap.empty) throw new Error('Selected pipeline stage no longer exists. Refresh CRM and choose an active stage.');
}

async function assertUniqueStageName(name, currentId = '') {
  const normalized = cleanText(name).toLowerCase();
  const snap = await getDocs(collection(db, collections.stages));
  if (snap.docs.some((row) => row.id !== currentId && cleanText(row.data()?.name).toLowerCase() === normalized)) throw new Error('A pipeline stage with this name already exists.');
}

async function assertUniqueIntegrationName(name, currentId = '') {
  const normalized = cleanText(name).toLowerCase();
  if (!normalized) return;
  const snap = await getDocs(collection(db, collections.integrations));
  if (snap.docs.some((row) => row.id !== currentId && row.data()?.isDeleted !== true && cleanText(row.data()?.name).toLowerCase() === normalized)) throw new Error('A CRM integration with this name already exists.');
}

function validateHttpsUrl(value, label) {
  const url = cleanText(value);
  if (!url) return '';
  if (!/^https:\/\//i.test(url) && !/^http:\/\/localhost(?::\d+)?(?:\/|$)/i.test(url)) throw new Error(`${label} must use HTTPS.`);
  return url;
}

export async function list(kind) {
  await ensure();
  const name = collections[kind];
  if (!name) throw new Error('Invalid CRM collection.');
  const snap = await getDocs(collection(db, name));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() })).filter((row) => row.isDeleted !== true).sort((a, b) => {
    if (kind === 'stages') return Number(a.sortOrder || 0) - Number(b.sortOrder || 0) || String(a.name || '').localeCompare(String(b.name || ''));
    return timestampValue(b.updatedAt || b.createdAt || b.occurredAt) - timestampValue(a.updatedAt || a.createdAt || a.occurredAt)
      || String(a.name || a.title || '').localeCompare(String(b.name || b.title || ''));
  });
}

export async function save(kind, value) {
  const uid = await ensure();
  const name = collections[kind];
  if (!name) throw new Error('Invalid CRM collection.');
  const ref = value.id ? doc(db, name, value.id) : doc(collection(db, name));
  const payload = { ...value, updatedAt: serverTimestamp(), updatedBy: uid };
  delete payload.id;

  if (['leads', 'contacts', 'deals', 'tasks'].includes(kind)) await assertActiveOwner(cleanText(value.ownerId));
  if (kind === 'leads') {
    payload.name = cleanText(value.name);
    if (!payload.name) throw new Error('Lead name is required.');
    payload.email = cleanText(value.email).toLowerCase();
    payload.phone = cleanText(value.phone);
    payload.stage = cleanText(value.stage);
    await assertStage(payload.stage);
    payload.score = Math.max(0, Math.min(100, safeNumber(value.score, 0)));
    payload.expectedValue = Math.max(0, safeNumber(value.expectedValue, 0));
    payload.ownerId = cleanText(value.ownerId);
  } else if (kind === 'contacts') {
    payload.name = cleanText(value.name);
    if (!payload.name) throw new Error('Contact name is required.');
    payload.email = cleanText(value.email).toLowerCase();
    payload.phone = cleanText(value.phone);
    payload.ownerId = cleanText(value.ownerId);
  } else if (kind === 'deals') {
    payload.name = cleanText(value.name);
    if (!payload.name) throw new Error('Deal name is required.');
    payload.stage = cleanText(value.stage);
    await assertStage(payload.stage);
    payload.value = Math.max(0, safeNumber(value.value, 0));
    payload.probability = Math.max(0, Math.min(100, safeNumber(value.probability, 0)));
    payload.currency = cleanText(value.currency || 'EUR').toUpperCase();
    if (!/^[A-Z]{3}$/.test(payload.currency)) throw new Error('Deal currency must be a 3-letter code such as EUR, USD or INR.');
    payload.ownerId = cleanText(value.ownerId);
  } else if (kind === 'tasks') {
    payload.title = cleanText(value.title);
    if (!payload.title) throw new Error('Task title is required.');
    payload.ownerId = cleanText(value.ownerId);
    payload.status = ['open', 'in_progress', 'done'].includes(String(value.status || 'open')) ? String(value.status || 'open') : 'open';
    payload.priority = ['low', 'medium', 'high'].includes(String(value.priority || 'medium')) ? String(value.priority || 'medium') : 'medium';
  } else if (kind === 'integrations') {
    payload.name = cleanText(value.name);
    if (!payload.name) throw new Error('Integration name is required.');
    await assertUniqueIntegrationName(payload.name, value.id);
    payload.baseUrl = validateHttpsUrl(value.baseUrl, 'CRM API base URL');
    payload.testUrl = validateHttpsUrl(value.testUrl, 'CRM test URL');
    payload.testMethod = ['GET', 'POST'].includes(String(value.testMethod || 'GET').toUpperCase()) ? String(value.testMethod || 'GET').toUpperCase() : 'GET';
  }

  if (kind === 'stages') {
    payload.name = String(value.name || '').trim();
    payload.sortOrder = Number(value.sortOrder || 0);
    if (!payload.name) throw new Error('Pipeline stage name is required.');
    await assertUniqueStageName(payload.name, value.id);
    if (value.id) {
      const existing = await getDoc(doc(db, name, value.id));
      const previousName = String(existing.data()?.name || '').trim();
      if (existing.exists() && previousName && previousName !== payload.name) {
        const [leadUse, dealUse] = await Promise.all([
          getDocs(query(collection(db, collections.leads), where('stage', '==', previousName), limit(1))),
          getDocs(query(collection(db, collections.deals), where('stage', '==', previousName), limit(1))),
        ]);
        if (!leadUse.empty || !dealUse.empty) throw new Error(`Pipeline stage “${previousName}” is in use. Move its leads/deals before renaming it.`);
      }
    }
  }
  await setDoc(ref, { ...payload, createdAt: value.createdAt || serverTimestamp() }, { merge: true });
  return { id: ref.id, ...payload };
}

export async function remove(kind, id) {
  await ensure();
  const name = collections[kind];
  if (!name) throw new Error('Invalid CRM collection.');
  if (kind === 'stages') {
    const stageSnap = await getDoc(doc(db, name, id));
    if (!stageSnap.exists()) return { success: true };
    const stageName = String(stageSnap.data()?.name || '').trim();
    if (stageName) {
      const [leadUse, dealUse] = await Promise.all([
        getDocs(query(collection(db, collections.leads), where('stage', '==', stageName), limit(1))),
        getDocs(query(collection(db, collections.deals), where('stage', '==', stageName), limit(1))),
      ]);
      if (!leadUse.empty || !dealUse.empty) throw new Error(`Pipeline stage “${stageName}” is in use. Move its leads/deals before deleting it.`);
    }
  }
  if (['leads', 'contacts', 'deals', 'tasks'].includes(kind)) {
    await setDoc(doc(db, name, id), { isDeleted: true, deletedAt: serverTimestamp(), deletedBy: auth.currentUser?.uid || '', updatedAt: serverTimestamp() }, { merge: true });
  } else {
    await deleteDoc(doc(db, name, id));
  }
  return { success: true };
}

export async function addActivity(activity) {
  return save('activities', { ...activity, occurredAt: serverTimestamp() });
}

export async function testIntegration(integration) {
  await ensure();
  const endpoint = String(integration.testUrl || integration.baseUrl || '').trim();
  if (!endpoint) throw new Error('API base/test URL is required.');
  if (!/^https:\/\//i.test(endpoint) && !/^http:\/\/localhost(?::\d+)?(?:\/|$)/i.test(endpoint)) throw new Error('CRM endpoint must use HTTPS.');
  const headers = { 'Content-Type': 'application/json' };
  const token = integration.apiKey || integration.token || '';
  if (token) headers.Authorization = integration.authScheme ? `${integration.authScheme} ${token}` : `Bearer ${token}`;
  const response = await fetch(endpoint, { method: integration.testMethod || 'GET', headers });
  const body = await response.text();
  if (!response.ok) throw new Error(`CRM returned HTTP ${response.status}${body ? `: ${body.slice(0, 160)}` : ''}`);
  return { success: true, status: response.status };
}

export default { list, save, remove, addActivity, testIntegration };
